import React, { Dispatch, useCallback, useEffect, useState } from 'react';

import { Dropdown } from 'components/reusable/Dropdown';
import {
  addNotification,
  changeNotification,
  deleteNotification,
  getNotifications,
} from 'data/notificationData';
import { EditNotifications } from './EditNotifications';
import { NotificationType } from 'types/notification';
import { objectIsEqual } from 'utils/helpers';
import { useAppDispatch, useAppSelector } from 'hooks/hooks';
import { add, change, remove, replace } from 'hooks/notificationsSlice';

const EDIT_ID = 'NEW';

export function Notifications({
  styles,
  confirmAction,
  displayError,
}: {
  styles: any;
  confirmAction: Dispatch<any>;
  displayError: Dispatch<any>;
}) {
  const notifications = useAppSelector((state) => state.notifications);
  const dispatch = useAppDispatch();
  const [originalNotifications, setOriginalNotifications] = useState(null);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const updateNotifications = useCallback(async () => {
    console.log('----------- GETTING NOTIFICATIONS DATA -----------');
    getNotifications().then((data) => {
      dispatch(
        replace({
          notifications: data.sort((a, b) => {
            return a.timestamp > b.timestamp ? -1 : 1;
          }),
        }),
      );
      setOriginalNotifications(data);
    });
  }, [dispatch]);

  const addNewNotification = useCallback(() => {
    const newNotification = {
      id: EDIT_ID,
      title: '',
      description: '',
      level: 0,
      latlng: null,
      occurrence: null,
      occur: null,
      timestamp: Date.now(),
    };
    dispatch(
      add({
        notification: newNotification,
      }),
    );
  }, [dispatch]);

  const actualDeleteRoute = useCallback(
    (id: string) => {
      return deleteNotification(id)
        .then(updateNotifications)
        .catch(() =>
          displayError('An error has occurred please try again later.'),
        );
    },
    [updateNotifications, displayError],
  );

  const confirmDelete = useCallback(
    (notification: NotificationType) => {
      confirmAction({
        title: 'Delete Notification',
        name: `"${notification.title}"`,
        delete: () => actualDeleteRoute(notification.id),
      });
    },
    [confirmAction, actualDeleteRoute],
  );

  const cancelChanges = useCallback(
    (notification: NotificationType) => {
      if (notification.id === EDIT_ID) {
        dispatch(remove({ notification }));
      } else {
        const original = originalNotifications.find(
          (r: NotificationType) => r.id === notification.id,
        );
        dispatch(change({ notification: original }));
      }
    },
    [originalNotifications, dispatch],
  );

  const saveNotification = useCallback(
    (notification: NotificationType) => {
      if (notification.id === EDIT_ID) {
        return addNotification(notification)
          .then(updateNotifications)
          .catch(() =>
            displayError('An error has occurred please try again later.'),
          );
      }

      return changeNotification(notification)
        .then(updateNotifications)
        .catch(() =>
          displayError('An error has occurred please try again later.'),
        );
    },
    [updateNotifications, displayError],
  );

  const hasChanged = useCallback(
    (notification: NotificationType) => {
      if (originalNotifications) {
        const original = originalNotifications.find(
          (o: NotificationType) => o.id === notification.id,
        );
        objectIsEqual(original, notification);
        return !objectIsEqual(original, notification);
      }
    },
    [originalNotifications],
  );

  useEffect(() => {
    updateNotifications();
  }, [updateNotifications]);

  return (
    <Dropdown
      title={'Notifications'}
      isCollapsed={isCollapsed}
      setCollapsed={() => setIsCollapsed(!isCollapsed)}
    >
      {notifications && (
        <EditNotifications
          styles={styles}
          notifications={notifications.value}
          deleteNotification={confirmDelete}
          hasChanged={hasChanged}
          cancelChanges={cancelChanges}
          saveNotification={saveNotification}
          addNotification={addNewNotification}
        />
      )}
    </Dropdown>
  );
}
